import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MapInstanceFilter, SeleccionadosBarraLateral } from '../../interfaces';
import {
  FuncionActivo,
  IActivo,
  ICliente,
  IFilter,
  IGrupo,
  IQueryParam,
  IRecorrido,
  IUbicacion,
  TipoVehiculo,
} from 'modelos/src';
import { LoginService } from '../../../../../login/login.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { Subscription } from 'rxjs';
import { MapaVehiculosService } from '../../mapaVehiculos.service';
import { ClientesJerarquiaService } from '../services/clientes-jerarquia.service';
import { clientModules } from '../../../../../../componentes/selectores/cliente-selector/cliente-modulos.util';

@Component({
  selector: 'app-filtros-vehiculos',
  templateUrl: './filtros-vehiculos.component.html',
  styleUrl: './filtros-vehiculos.component.scss',
  standalone: false,
})
export class FiltrosVehiculoComponent implements OnInit, OnDestroy, OnChanges {
  private lista2 = inject(Lista2Service);
  private mapaService = inject(MapaVehiculosService);
  private jerarquia = inject(ClientesJerarquiaService);
  dialogRef = inject<MatDialogRef<FiltrosVehiculoComponent>>(MatDialogRef, {
    optional: true,
  });
  data = inject(MAT_DIALOG_DATA, { optional: true });

  @Input() public seleccionadoBarraLateral?: SeleccionadosBarraLateral;

  // Getter para reportes desde el servicio
  get reportes(): any[] {
    return this.mapaService.reportes;
  }

  // Estado de los paneles - pueden estar abiertos simultáneamente, inicialmente cerrados
  public panelExpanded = false;
  public panelUbicacionesExpanded = false;
  public panelRecorridosExpanded = false;

  // Vehículos (todos)
  public vehiculos?: IActivo[] = [];
  public vehiculos$?: Subscription;

  // Vehículos filtrables (según clientes/grupos seleccionados)
  public vehiculosFiltrables: IActivo[] = [];

  // Grupos (todos)
  public grupos?: IGrupo[] = [];
  public grupos$?: Subscription;

  // Grupos filtrados según clientes seleccionados
  public gruposFiltrados: IGrupo[] = [];

  //Ubicaciones de vehículos
  public ubicaciones?: IUbicacion[] = [];
  public ubicaciones$?: Subscription;

  // Ubicaciones filtrables (según clientes seleccionados)
  public ubicacionesFiltrables: IUbicacion[] = [];

  // Recorridos (todos)
  public recorridos?: IRecorrido[] = [];
  public recorridos$?: Subscription;

  // Recorridos filtrables (según clientes seleccionados)
  public recorridosFiltrables: IRecorrido[] = [];

  // FILTRO ÚNICO DE CLIENTES: aplica a vehículos, ubicaciones y recorridos.
  // Sólo se listan (y sólo se expanden por jerarquía) los clientes con el
  // módulo de vehículos activo. Es un campo y no un literal en el template
  // para no crear un array nuevo en cada ciclo de detección de cambios.
  public readonly modulosCliente: clientModules[] = ['moduloVehiculos'];
  public selectedClienteIds: string[] = [];
  // Incluir los clientes hijos de los seleccionados. Arranca prendido: el mapa
  // se abre mostrando el cliente del permiso y toda su jerarquía.
  public includeChildren = true;
  // Selección expandida con los hijos (lo que efectivamente se filtra).
  private idsClientesEfectivos: string[] = [];

  // Selecciones múltiples (sin acumulación entre categorías)
  public selectedGrupoIds: string[] = [];
  public selectedFunciones: FuncionActivo[] = [];
  public selectedTiposVehiculo: TipoVehiculo[] = [];
  public selectedVehiculoIds: string[] = [];

  // Solo vehículos encendidos (ignición === true)
  public soloEncendidos = false;

  // Selecciones para ubicaciones
  public selectedUbicacionIds: string[] = [];

  // Selecciones para recorridos
  public selectedRecorridoIds: string[] = [];

  public funcionesDisponibles: FuncionActivo[] = [
    'Transporte',
    'Bomberos',
    'Mantenimiento',
    'Policia',
    'Particular',
    'Ambulancia',
    'Seguridad Privada',
    'Servicio Técnico',
    'Otro',
  ];
  public funcionesLabel: Record<FuncionActivo, string> = {
    Particular: 'Particular',
    'Seguridad Privada': 'Seguridad Privada',
    Mantenimiento: 'Mantenimiento',
    Transporte: 'Transporte',
    Bomberos: 'Bomberos',
    Policia: 'Policía',
    Ambulancia: 'Ambulancia',
    'Servicio Técnico': 'Servicio Técnico',
    Otro: 'Otro',
  };

  public tiposVehiculoDisponibles: TipoVehiculo[] = [
    'Auto',
    'Camion',
    'Camioneta',
    'Colectivo',
    'Grua',
    'Moto',
    'Otro',
  ];
  public tiposLabel: Record<TipoVehiculo, string> = {
    Auto: 'Auto',
    Camion: 'Camión',
    Camioneta: 'Camioneta',
    Colectivo: 'Colectivo',
    Grua: 'Grúa',
    Moto: 'Moto',
    Otro: 'Otro',
  };

  public getTipoLabel(value: string): string {
    return this.tiposLabel[value as TipoVehiculo] ?? value;
  }

  public getFuncionLabel(value: string): string {
    return this.funcionesLabel[value as FuncionActivo] ?? value;
  }

  public clientePropio: ICliente = LoginService.getClientePropio();

  constructor() {
    const data = this.data;

    // Si se usa en un dialog, usar los datos del dialog
    if (data?.seleccionadoBarraLateral) {
      this.seleccionadoBarraLateral = data.seleccionadoBarraLateral;
    }
  }

  // Inicialización
  public async ngOnInit(): Promise<void> {
    // El árbol de clientes se necesita para resolver `includeChildren`.
    await this.jerarquia.cargar();

    // Si viene con filtros guardados, restaurarlos; si no, arrancar filtrando
    // por el cliente actual del permiso.
    if (this.seleccionadoBarraLateral?.filtrosSeleccionados) {
      this.cargarFiltrosIniciales();
    } else {
      this.aplicarFiltroClientePorDefecto();
    }

    this.escucharVehiculos();
    this.listarGrupos();
    this.escucharUbicaciones();
    this.escucharRecorridos();
  }

  // Cargar filtros iniciales desde seleccionadoBarraLateral
  private cargarFiltrosIniciales(): void {
    const filtros = this.seleccionadoBarraLateral?.filtrosSeleccionados;
    if (!filtros) return;

    this.selectedClienteIds =
      filtros.idsClientes || filtros.idsClientesVehiculos || [];
    this.includeChildren = filtros.includeChildren || false;
    this.selectedGrupoIds = filtros.gruposVehiculos || [];
    this.selectedFunciones = filtros.funcionVehiculos || [];
    this.selectedTiposVehiculo = filtros.tiposVehiculo || [];
    this.selectedVehiculoIds = filtros.idsVehiculos || [];
    this.soloEncendidos = filtros.soloEncendidos || false;
    this.selectedUbicacionIds = filtros.idsUbicaciones || [];
    this.selectedRecorridoIds = filtros.idsRecorridos || [];

    // Actualizar las listas derivadas
    this.recalcularClientesEfectivos();
    this.updateDerivedOptions();
    this.updateUbicacionesFiltrables();
    this.updateRecorridosFiltrables();
  }

  // Estado inicial: el cliente del permiso activo y su jerarquía
  private aplicarFiltroClientePorDefecto(): void {
    const idCliente = ClientesJerarquiaService.idClienteActual();
    this.selectedClienteIds = idCliente ? [idCliente] : [];
    this.includeChildren = true;
    this.recalcularClientesEfectivos();
    this.aplicarFiltros();
  }

  public ngOnDestroy(): void {
    this.vehiculos$?.unsubscribe();
    this.grupos$?.unsubscribe();
    this.ubicaciones$?.unsubscribe();
    this.recorridos$?.unsubscribe();
  }

  // LISTAR
  /**
   * Los vehículos salen de los reportes del mapa, no de una consulta propia:
   * así el panel cuenta exactamente el mismo universo que se dibuja (una
   * consulta aparte quedaba paginada y mostraba menos vehículos que el mapa).
   */
  private escucharVehiculos(): void {
    this.vehiculos$?.unsubscribe();
    this.vehiculos$ = this.mapaService.reportes$.subscribe((reportes) => {
      const porId = new Map<string, IActivo>();
      for (const reporte of reportes) {
        const activo = reporte.activo as IActivo | undefined;
        if (activo?._id) porId.set(activo._id, activo);
      }
      this.vehiculos = [...porId.values()];

      // Inicializar vehiculosFiltrables según selección actual de clientes/grupos
      this.updateDerivedOptions();
      this.aplicarFiltros();
    });
  }

  private async listarGrupos(): Promise<void> {
    const filter: IFilter<IGrupo> = {
      categoria: 'Flota',
    };

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'nombre',
      select: 'idCliente nombre color',
      includeChildren: true,
      unlimited: true,
    };

    this.grupos$?.unsubscribe();
    this.grupos$ = this.lista2.grupos(query, 30).subscribe((data) => {
      this.grupos = data.datos || [];
      // Actualizar grupos filtrados según clientes seleccionados
      this.updateDerivedOptions();
    });
  }

  // Mismo criterio que los vehículos: las capas salen de lo que ya cargó el mapa.
  private escucharUbicaciones(): void {
    this.ubicaciones$?.unsubscribe();
    this.ubicaciones$ = this.mapaService.ubicaciones$.subscribe(
      (ubicaciones) => {
        this.ubicaciones = ubicaciones || [];
        this.updateUbicacionesFiltrables();
        this.aplicarFiltros();
      },
    );
  }

  private escucharRecorridos(): void {
    this.recorridos$?.unsubscribe();
    this.recorridos$ = this.mapaService.recorridos$.subscribe((recorridos) => {
      this.recorridos = recorridos || [];
      this.updateRecorridosFiltrables();
      this.aplicarFiltros();
    });
  }

  // ==========================================
  // FILTRO ÚNICO DE CLIENTES
  // ==========================================

  private recalcularClientesEfectivos(): void {
    this.idsClientesEfectivos = this.jerarquia.expandir(
      this.selectedClienteIds,
      this.includeChildren,
      this.modulosCliente,
    );
  }

  /** Sin clientes seleccionados no se filtra por cliente (se ve todo el scope). */
  private coincideCliente(idCliente?: string): boolean {
    if (!this.idsClientesEfectivos.length) return true;
    return !!idCliente && this.idsClientesEfectivos.includes(idCliente);
  }

  /** Cambió la selección de clientes o el flag de hijos: reaplicar en las 3 secciones. */
  public aplicarFiltrosClientes(): void {
    this.recalcularClientesEfectivos();
    this.updateDerivedOptions();
    this.updateUbicacionesFiltrables();
    this.updateRecorridosFiltrables();
    this.aplicarFiltros();
  }

  /** Vuelve al estado inicial: cliente del permiso y su jerarquía. */
  public limpiarFiltrosClientes(): void {
    const idCliente = ClientesJerarquiaService.idClienteActual();
    this.selectedClienteIds = idCliente ? [idCliente] : [];
    this.includeChildren = true;
    this.aplicarFiltrosClientes();
  }

  /**
   * Actualiza las listas derivadas (gruposFiltrados y vehiculosFiltrables)
   * y depura selecciones que queden inválidas.
   */
  private updateDerivedOptions(): void {
    // Asegurar arrays definidos
    this.grupos = this.grupos || [];
    this.vehiculos = this.vehiculos || [];

    // Grupos del/los cliente/s seleccionado/s
    this.gruposFiltrados = this.grupos.filter((g) =>
      this.coincideCliente(g.idCliente),
    );

    // Vehículos del/los cliente/s seleccionado/s, acotados por grupo si hay
    const gruposActive =
      this.selectedGrupoIds && this.selectedGrupoIds.length > 0;

    this.vehiculosFiltrables = this.vehiculos.filter((v) => {
      if (!this.coincideCliente(v.idCliente)) return false;
      if (gruposActive) return this.selectedGrupoIds.includes(v.idGrupo ?? '');
      return true;
    });

    // Depurar selectedGrupoIds que ya no existan en gruposFiltrados
    // Solo depurar si hay grupos cargados
    if (
      this.selectedGrupoIds &&
      this.selectedGrupoIds.length > 0 &&
      this.grupos.length > 0
    ) {
      const grupoIdsValidos = new Set(this.gruposFiltrados.map((g) => g._id));
      const nuevosSelectedGrupos = this.selectedGrupoIds.filter((id) =>
        grupoIdsValidos.has(id),
      );
      if (nuevosSelectedGrupos.length !== this.selectedGrupoIds.length) {
        this.selectedGrupoIds = nuevosSelectedGrupos;
      }
    }

    // Depurar selectedVehiculoIds que ya no estén en vehiculosFiltrables
    // Solo depurar si hay vehículos cargados
    if (
      this.selectedVehiculoIds &&
      this.selectedVehiculoIds.length > 0 &&
      this.vehiculos.length > 0
    ) {
      const vehIdsValidos = new Set(this.vehiculosFiltrables.map((v) => v._id));
      const nuevosSelectedVehiculos = this.selectedVehiculoIds.filter((id) =>
        vehIdsValidos.has(id),
      );
      if (nuevosSelectedVehiculos.length !== this.selectedVehiculoIds.length) {
        this.selectedVehiculoIds = nuevosSelectedVehiculos;
      }
    }
  }

  // FILTROS
  public aplicarFiltros(): void {
    // Primero actualizar listas derivadas y depurar selecciones
    this.recalcularClientesEfectivos();
    this.updateDerivedOptions();

    let vehiculosFiltrados = [...(this.vehiculos || [])];

    // Filtro por clientes (único, ya expandido con los hijos si corresponde)
    vehiculosFiltrados = vehiculosFiltrados.filter((v) =>
      this.coincideCliente(v.idCliente),
    );

    // Filtro por grupos (múltiple)
    if (this.selectedGrupoIds && this.selectedGrupoIds.length > 0) {
      vehiculosFiltrados = vehiculosFiltrados.filter((v) =>
        this.selectedGrupoIds.includes(v.idGrupo ?? ''),
      );
    }

    // Filtro por funciones (múltiple)
    if (this.selectedFunciones && this.selectedFunciones.length > 0) {
      vehiculosFiltrados = vehiculosFiltrados.filter((v) =>
        this.selectedFunciones.includes(v.funcion),
      );
    }

    // Filtro por tipos de vehículo (múltiple)
    if (this.selectedTiposVehiculo && this.selectedTiposVehiculo.length > 0) {
      vehiculosFiltrados = vehiculosFiltrados.filter((v) =>
        this.selectedTiposVehiculo.includes(v.vehiculo?.tipo),
      );
    }

    // Filtro por vehículos específicos (múltiple)
    if (this.selectedVehiculoIds && this.selectedVehiculoIds.length > 0) {
      vehiculosFiltrados = vehiculosFiltrados.filter((v) =>
        this.selectedVehiculoIds.includes(v._id ?? ''),
      );
    }

    // La lista concreta de vehículos sólo se publica cuando hay filtros más
    // finos que el de clientes. Si sólo se filtra por cliente, se deja vacía
    // para que el mapa lo evalúe contra cada reporte (los vehículos que llegan
    // después por polling/WS también entran sin reaplicar los filtros).
    const hayFiltrosPropios =
      (this.selectedGrupoIds?.length || 0) > 0 ||
      (this.selectedFunciones?.length || 0) > 0 ||
      (this.selectedTiposVehiculo?.length || 0) > 0 ||
      (this.selectedVehiculoIds?.length || 0) > 0;

    const ids = hayFiltrosPropios
      ? vehiculosFiltrados
          .map((v) => v._id)
          .filter((_id) => _id !== undefined && _id !== null)
      : [];

    // Filtrar ubicaciones (mismo filtro de clientes)
    let ubicacionesFiltradas = (this.ubicaciones || []).filter((u) =>
      this.coincideCliente(u.idCliente),
    );

    if (this.selectedUbicacionIds && this.selectedUbicacionIds.length > 0) {
      ubicacionesFiltradas = ubicacionesFiltradas.filter((u) =>
        this.selectedUbicacionIds.includes(u._id ?? ''),
      );
    }

    const idsUbicaciones = ubicacionesFiltradas
      .map((u) => u._id)
      .filter((_id) => _id !== undefined && _id !== null);

    // Filtrar recorridos (mismo filtro de clientes)
    let recorridosFiltrados = (this.recorridos || []).filter((r) =>
      this.coincideCliente(r.idCliente),
    );

    if (this.selectedRecorridoIds && this.selectedRecorridoIds.length > 0) {
      recorridosFiltrados = recorridosFiltrados.filter((r) =>
        this.selectedRecorridoIds.includes(r._id ?? ''),
      );
    }

    const idsRecorridos = recorridosFiltrados
      .map((r) => r._id)
      .filter((_id) => _id !== undefined && _id !== null);

    const filter: MapInstanceFilter = {
      idsClientes: [...this.selectedClienteIds],
      includeChildren: this.includeChildren,
      idsVehiculosFiltrados: ids,
      idsVehiculos: this.selectedVehiculoIds,
      // El filtro de clientes es único: los tres campos llevan lo mismo.
      idsClientesVehiculos: [...this.idsClientesEfectivos],
      idsClientesUbicaciones: [...this.idsClientesEfectivos],
      idsClientesRecorridos: [...this.idsClientesEfectivos],
      funcionVehiculos: this.selectedFunciones,
      tiposVehiculo: this.selectedTiposVehiculo,
      gruposVehiculos: this.selectedGrupoIds,
      idsUbicaciones: this.selectedUbicacionIds,
      idsUbicacionesFiltradas: idsUbicaciones,
      idsRecorridos: this.selectedRecorridoIds,
      idsRecorridosFiltrados: idsRecorridos,
      soloEncendidos: this.soloEncendidos,
    };

    // Guardar en filtros globales (para retrocompatibilidad)
    this.mapaService.setFiltrosActual(filter);

    // Guardar en seleccionadosBarraLateral para que se aplique en el mapa
    this.mapaService.updateSeleccionadoBarraLateral({
      filtrosSeleccionados: filter,
    });
  }

  // Limpiar los filtros propios de vehículos (el de clientes es compartido)
  public limpiarFiltros(): void {
    this.selectedGrupoIds = [];
    this.selectedFunciones = [];
    this.selectedTiposVehiculo = [];
    this.selectedVehiculoIds = [];
    this.soloEncendidos = false;
    this.aplicarFiltros();
  }

  // Contador de vehículos filtrados
  public get vehiculosFiltradosCount(): number {
    if (!this.vehiculos) return 0;

    let resultado = this.vehiculos.filter((v) =>
      this.coincideCliente(v.idCliente),
    );

    if (this.selectedGrupoIds && this.selectedGrupoIds.length > 0) {
      resultado = resultado.filter((v) =>
        this.selectedGrupoIds.includes(v.idGrupo ?? ''),
      );
    }

    if (this.selectedFunciones && this.selectedFunciones.length > 0) {
      resultado = resultado.filter(
        (v) => v.funcion && this.selectedFunciones.includes(v.funcion),
      );
    }

    if (this.selectedTiposVehiculo && this.selectedTiposVehiculo.length > 0) {
      resultado = resultado.filter(
        (v) =>
          v.vehiculo?.tipo &&
          this.selectedTiposVehiculo.includes(v.vehiculo.tipo),
      );
    }

    if (this.selectedVehiculoIds && this.selectedVehiculoIds.length > 0) {
      resultado = resultado.filter((v) =>
        this.selectedVehiculoIds.includes(v._id ?? ''),
      );
    }

    // Solo encendidos: cruzar con los reportes (la ignición vive en el reporte).
    if (this.soloEncendidos) {
      const encendidos = new Set(
        this.reportes
          .filter((r) => (r?.valores as any)?.ignicion === true)
          .map((r) => r?.activo?._id)
          .filter((id) => !!id),
      );
      resultado = resultado.filter((v) => encendidos.has(v._id));
    }

    return resultado.length;
  }

  // Total de filtros activos (sin contar el de clientes, que es compartido)
  public get totalFiltrosActivos(): number {
    return (
      (this.selectedGrupoIds?.length || 0) +
      (this.selectedFunciones?.length || 0) +
      (this.selectedTiposVehiculo?.length || 0) +
      (this.selectedVehiculoIds?.length || 0) +
      (this.soloEncendidos ? 1 : 0)
    );
  }

  // Total de clientes seleccionados (contando la expansión por hijos)
  public get totalClientesSeleccionados(): number {
    return this.idsClientesEfectivos.length;
  }

  // Toggle panel
  public togglePanel(): void {
    this.panelExpanded = !this.panelExpanded;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Los vehículos se refrescan solos: llegan por la suscripción a reportes$.
    if (changes['seleccionadoBarraLateral']) {
      const change = changes['seleccionadoBarraLateral'];

      if (!change) return;
      const filtrosAhora = change?.currentValue?.filtrosSeleccionados;
      const filtrosAntes = change?.previousValue?.filtrosSeleccionados;

      // Evitar procesar si no hubo cambios reales en los filtros
      if (JSON.stringify(filtrosAhora) === JSON.stringify(filtrosAntes)) {
        return;
      }

      // Solo actualizar si hay filtros explícitos
      if (filtrosAhora) {
        this.selectedClienteIds =
          filtrosAhora.idsClientes || filtrosAhora.idsClientesVehiculos || [];
        this.includeChildren = filtrosAhora.includeChildren || false;
        this.selectedGrupoIds = filtrosAhora.gruposVehiculos || [];
        this.selectedFunciones = filtrosAhora.funcionVehiculos || [];
        this.selectedTiposVehiculo = filtrosAhora.tiposVehiculo || [];
        this.selectedVehiculoIds = filtrosAhora.idsVehiculos || [];
        this.soloEncendidos = filtrosAhora.soloEncendidos || false;
        this.selectedUbicacionIds = filtrosAhora.idsUbicaciones || [];
        this.selectedRecorridoIds = filtrosAhora.idsRecorridos || [];

        // Aseguramos que las listas derivadas se actualicen antes de emitir/aplicar filtros
        this.recalcularClientesEfectivos();
        this.updateDerivedOptions();
        this.updateUbicacionesFiltrables();
        this.updateRecorridosFiltrables();
      }
      // Si filtrosAhora es undefined pero filtrosAntes tenía valores,
      // NO limpiar automáticamente - mantener el estado actual
      // Solo limpiar si explícitamente se pasan arrays vacíos
    }
  }

  // ==========================================
  // MÉTODOS PARA FILTROS DE UBICACIONES
  // ==========================================

  // Actualizar ubicaciones filtrables según clientes seleccionados
  private updateUbicacionesFiltrables(): void {
    this.ubicacionesFiltrables = (this.ubicaciones || []).filter((u) =>
      this.coincideCliente(u.idCliente),
    );
  }

  // Actualizar recorridos filtrables según clientes seleccionados
  private updateRecorridosFiltrables(): void {
    this.recorridosFiltrables = (this.recorridos || []).filter((r) =>
      this.coincideCliente(r.idCliente),
    );
  }

  // Aplicar filtros de ubicaciones
  public aplicarFiltrosUbicaciones(): void {
    this.updateUbicacionesFiltrables();
    this.aplicarFiltros();
  }

  // Aplicar filtros de recorridos
  public aplicarFiltrosRecorridos(): void {
    this.updateRecorridosFiltrables();
    this.aplicarFiltros();
  }

  // Limpiar las capas del mapa: ubicaciones y recorridos (el de clientes es compartido)
  public limpiarFiltrosCapas(): void {
    this.selectedUbicacionIds = [];
    this.selectedRecorridoIds = [];
    this.updateUbicacionesFiltrables();
    this.updateRecorridosFiltrables();
    this.aplicarFiltros();
  }

  // Contador de ubicaciones filtradas
  public get ubicacionesFiltradasCount(): number {
    if (!this.ubicaciones) return 0;

    let resultado = this.ubicaciones.filter((u) =>
      this.coincideCliente(u.idCliente),
    );

    if (this.selectedUbicacionIds && this.selectedUbicacionIds.length > 0) {
      resultado = resultado.filter((u) =>
        this.selectedUbicacionIds.includes(u._id ?? ''),
      );
    }

    return resultado.length;
  }

  // Total de filtros activos de la sección de capas (ubicaciones + recorridos)
  public get totalFiltrosActivosCapas(): number {
    return (
      (this.selectedUbicacionIds?.length || 0) +
      (this.selectedRecorridoIds?.length || 0)
    );
  }

  // Contador de recorridos filtrados
  public get recorridosFiltradosCount(): number {
    if (!this.recorridos) return 0;

    let resultado = this.recorridos.filter((r) =>
      this.coincideCliente(r.idCliente),
    );

    if (this.selectedRecorridoIds && this.selectedRecorridoIds.length > 0) {
      resultado = resultado.filter((r) =>
        this.selectedRecorridoIds.includes(r._id ?? ''),
      );
    }

    return resultado.length;
  }
}
